import { JSONUtils } from '../../utility/json-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import {
  cloneData, cloneListOfCloneable, isSameData, isSameList,
} from '../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { Cardinal } from '../core/cardinal.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { TicketCapacityGroup } from './ticket-capacity-group.js';
import { TicketTemplate } from './ticket-template/ticket-template.js';
import { ITicketPool } from './ticketing-state/ticket-pool.js';

export class EventTicketCapacityGroups extends DataModel {
  static readonly TYPE_ID = 'e04e981911234cf593f04269286258c7';

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string,
    public readonly groups: TicketCapacityGroup[],
  ) {
    super();
  }

  get $typeId(): string {
    return EventTicketCapacityGroups.TYPE_ID;
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    eventId: string,
    groups: TicketCapacityGroup[],
  }): EventTicketCapacityGroups {
    return new EventTicketCapacityGroups(
        args.businessRef,
        args.eventId,
        args.groups,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventTicketCapacityGroups {
    return EventTicketCapacityGroups.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      eventId: MapUtils.getString(map, 'eventId'),
      groups: MapUtils.getList(
          map,
          'groups',
          (value) => context.fromData<TicketCapacityGroup>(JSONUtils.decodeRecord(value))),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'groups': this.groups.map((group) => context.toData(group)),
    };
  }

  findGroup(ticketTemplateId: string): TicketCapacityGroup | null {
    return this.groups.find((group) => group.ticketTemplateIds.includes(ticketTemplateId)) ?? null;
  }

  getTicketCapacity(ticket: TicketTemplate): Cardinal {
    return this.findGroup(ticket.uniqueId)?.capacity ?? ticket.quantity;
  }

  getRemainingTicketCapacity(ticket: TicketTemplate, ticketPool: ITicketPool): Cardinal {
    const group = this.findGroup(ticket.uniqueId);
    if(group != null) return group.getRemainingCapacity(ticketPool);
    if(ticket.quantity.isInfinite) return Cardinal.infinity;

    return Cardinal.finite(Math.max(ticket.quantity.value - ticketPool.getTicketCount(ticket.uniqueId), 0));
  }

  getTotalCapacity(tickets: readonly TicketTemplate[]): Cardinal {
    return this.sumTicketCapacities(tickets, (ticket) => this.getTicketCapacity(ticket));
  }

  getTotalRemainingCapacity(tickets: readonly TicketTemplate[], ticketPool: ITicketPool): Cardinal {
    return this.sumTicketCapacities(tickets, (ticket) => this.getRemainingTicketCapacity(ticket, ticketPool));
  }

  private sumTicketCapacities(
      tickets: readonly TicketTemplate[],
      capacityForTicket: (ticket: TicketTemplate) => Cardinal,
  ): Cardinal {
    const countedGroupIds: string[] = [];
    let total = 0;

    for(const ticket of tickets) {
      const group = this.findGroup(ticket.uniqueId);
      if(group != null) {
        if(countedGroupIds.includes(group.uniqueId)) continue;
        countedGroupIds.push(group.uniqueId);
      }

      const capacity = capacityForTicket(ticket);
      if(capacity.isInfinite) return Cardinal.infinity;
      total += capacity.value;
    }

    return Cardinal.finite(total);
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    businessRef = cloneData(this.businessRef),
    eventId = this.eventId,
    groups = cloneListOfCloneable(this.groups),
  }: {
    businessRef?: BusinessModuleRef,
    eventId?: string,
    groups?: TicketCapacityGroup[],
  } = {}): this {
    return EventTicketCapacityGroups.create({
      businessRef,
      eventId,
      groups,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      other.eventId === this.eventId &&
      isSameList(other.groups, this.groups, isSameData);
  }
}

export class EventTicketCapacityGroupsBuilder extends DataModelBuilder<EventTicketCapacityGroups> {
  build(context: SerializationContext, map: SerializedData): EventTicketCapacityGroups {
    return EventTicketCapacityGroups.fromMap(context, map);
  }
}
